import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, PointElement, LineElement,
  Title, Filler
} from 'chart.js';

import GlobalStyles from './styles/GlobalStyles';
import useTheme from './hooks/useTheme';
import useToast from './hooks/useToast';

import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import ToastContainer from './components/ToastContainer';

import DashboardPage from './pages/DashboardPage';
import PipelinePage from './pages/PipelinePage';
import TasksPage from './pages/TasksPage';
import ContactsPage from './pages/ContactsPage';
import BillingPage from './pages/BillingPage';
import PreferencesPage from './pages/PreferencesPage';

import AddLeadModal from './components/modals/AddLeadModal';
import AddTaskModal from './components/modals/AddTaskModal';
import AddContactModal from './components/modals/AddContactModal';
import AddInvoiceModal from './components/modals/AddInvoiceModal';
import AddQuotationModal from './components/modals/AddQuotationModal';
import DeleteLeadModal from './components/modals/DeleteLeadModal';
import DownloadReportModal from './components/modals/DownloadReportModal';

import {
  leadsApi, tasksApi, contactsApi, invoicesApi, quotationsApi
} from './api/client';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title, Filler);
ChartJS.defaults.font.family = "'JetBrains Mono', monospace";
ChartJS.defaults.font.size = 10;

export default function App() {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [theme, setTheme] = useTheme();
  const { toasts, showToast } = useToast();

  // Data
  const [leads, setLeads] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotations, setQuotations] = useState([]);

  // Dashboard filter
  const [dashFilter, setDashFilter] = useState({ start: '2026-04-01', end: '2026-06-30' });

  // Preferences (localStorage, as in original)
  const [prefs] = useState({ compactMode: false, emailNotifs: true, language: 'id' });

  // Modal states
  const [modals, setModals] = useState({
    addLead: false, deleteLead: false, addTask: false,
    addContact: false, addInvoice: false, addQuotation: false,
    downloadReport: false
  });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const openModal = (name) => setModals(p => ({ ...p, [name]: true }));
  const closeModal = (name) => setModals(p => ({ ...p, [name]: false }));

  // Load data on mount
  useEffect(() => {
    const load = async () => {
      try {
        const [l, t, c, i, q] = await Promise.all([
          leadsApi.list(), tasksApi.list(), contactsApi.list(),
          invoicesApi.list(), quotationsApi.list()
        ]);
        setLeads(l); setTasks(t); setContacts(c); setInvoices(i); setQuotations(q);
      } catch (err) {
        console.error('Failed to load data:', err);
        showToast('Gagal memuat data dari server', 'error');
      }
    };
    load();
  }, [showToast]);

  // Clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const dateString = currentTime.toLocaleDateString(
    prefs.language === 'en' ? 'en-US' : 'id-ID',
    { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
  );

  // ─── LEAD ACTIONS ───
  const handleAddLead = async (form) => {
    try {
      const created = await leadsApi.create(form);
      setLeads(prev => [...prev, created]);
      showToast(`Lead "${created.name}" ditambahkan!`, 'success');
      closeModal('addLead');
    } catch (e) { showToast('Gagal menambah lead', 'error'); }
  };

  const handleMoveLead = async (id, col) => {
    const prev = leads;
    setLeads(curr => curr.map(l => l.id === id ? { ...l, col } : l));
    try {
      await leadsApi.update(id, { col });
      showToast(`Lead dipindahkan ke ${col.toUpperCase()}`, 'success');
    } catch (e) {
      setLeads(prev);
      showToast('Gagal memindahkan lead', 'error');
    }
  };

  const handleRequestDeleteLead = (lead) => {
    setDeleteTarget(lead);
    openModal('deleteLead');
  };

  const handleConfirmDeleteLead = async () => {
    if (!deleteTarget) return;
    try {
      await leadsApi.remove(deleteTarget.id);
      setLeads(prev => prev.filter(l => l.id !== deleteTarget.id));
      showToast('Lead berhasil dihapus', 'error');
    } catch (e) { showToast('Gagal menghapus lead', 'error'); }
    closeModal('deleteLead');
    setDeleteTarget(null);
  };

  // ─── TASK ACTIONS ───
  const handleAddTask = async (form) => {
    try {
      const created = await tasksApi.create(form);
      setTasks(prev => [...prev, created]);
      showToast('Task ditambahkan!', 'success');
      closeModal('addTask');
    } catch (e) { showToast('Gagal menambah task', 'error'); }
  };

  const handleToggleTask = async (task) => {
    const newDone = !task.done;
    const prev = tasks;
    setTasks(curr => curr.map(t => t.id === task.id ? { ...t, done: newDone } : t));
    try {
      await tasksApi.update(task.id, { done: newDone });
      if (newDone) showToast('Task diselesaikan!', 'success');
    } catch (e) {
      setTasks(prev);
      showToast('Gagal memperbarui task', 'error');
    }
  };

  const handleDeleteTask = async (id) => {
    try {
      await tasksApi.remove(id);
      setTasks(prev => prev.filter(t => t.id !== id));
      showToast('Task telah dihapus', 'info');
    } catch (e) { showToast('Gagal menghapus task', 'error'); }
  };

  // ─── CONTACT ACTIONS ───
  const handleAddContact = async (form) => {
    try {
      const created = await contactsApi.create(form);
      setContacts(prev => [created, ...prev]);
      showToast(`Kontak ${created.name} tersimpan!`, 'success');
      closeModal('addContact');
    } catch (e) { showToast('Gagal menambah kontak', 'error'); }
  };

  const handleDeleteContact = async (id) => {
    try {
      await contactsApi.remove(id);
      setContacts(prev => prev.filter(c => c.id !== id));
      showToast('Kontak dihapus', 'info');
    } catch (e) { showToast('Gagal menghapus kontak', 'error'); }
  };

  // ─── INVOICE ACTIONS ───
  const handleAddInvoice = async (form) => {
    try {
      const created = await invoicesApi.create(form);
      setInvoices(prev => [created, ...prev]);
      showToast(`Invoice ${created.id} dibuat!`, 'success');
      closeModal('addInvoice');
    } catch (e) { showToast('Gagal membuat invoice', 'error'); }
  };

  const handleDeleteInvoice = async (id) => {
    try {
      await invoicesApi.remove(id);
      setInvoices(prev => prev.filter(i => i.id !== id));
      showToast('Invoice dihapus', 'info');
    } catch (e) { showToast('Gagal menghapus invoice', 'error'); }
  };

  const handleInvoiceStatusChange = async (id, newStatus) => {
    const prev = invoices;
    setInvoices(curr => curr.map(inv => inv.id === id ? { ...inv, status: newStatus } : inv));
    try {
      await invoicesApi.update(id, { status: newStatus });
      showToast(`Status tagihan ${id} diperbarui menjadi ${newStatus.toUpperCase()}`, 'success');
    } catch (e) {
      setInvoices(prev);
      showToast('Gagal memperbarui status', 'error');
    }
  };

  // ─── QUOTATION ACTIONS ───
  const handleAddQuotation = async (form) => {
    try {
      const created = await quotationsApi.create(form);
      setQuotations(prev => [created, ...prev]);
      showToast(`Quotation ${created.id} berhasil dibuat!`, 'success');
      closeModal('addQuotation');
    } catch (e) { showToast('Gagal membuat quotation', 'error'); }
  };

  const handleDeleteQuotation = async (id) => {
    try {
      await quotationsApi.remove(id);
      setQuotations(prev => prev.filter(q => q.id !== id));
      showToast('Quotation dihapus', 'info');
    } catch (e) { showToast('Gagal menghapus quotation', 'error'); }
  };

  const handleQuotationStatusChange = async (id, newStatus) => {
    const prev = quotations;
    setQuotations(curr => curr.map(q => q.id === id ? { ...q, status: newStatus } : q));
    try {
      await quotationsApi.update(id, { status: newStatus });
      showToast(`Status quotation ${id} diperbarui menjadi ${newStatus.toUpperCase()}`, 'success');
    } catch (e) {
      setQuotations(prev);
      showToast('Gagal memperbarui status', 'error');
    }
  };

  // ─── REPORT ───
  const handleDownloadReport = (e) => {
    e.preventDefault();
    const startDate = e.target.startDate.value;
    const endDate = e.target.endDate.value;
    closeModal('downloadReport');
    showToast(`Mempersiapkan laporan dari ${startDate} hingga ${endDate}...`, 'info');
    setTimeout(() => showToast('Laporan berhasil diunduh!', 'success'), 2500);
  };

  return (
    <div className={`app-wrapper ${prefs.compactMode ? 'compact' : ''}`} data-testid="app-wrapper">
      <GlobalStyles />

      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        activeSection={activeSection}
        onSelect={setActiveSection}
        onUserClick={() => showToast('Profile info', 'info')}
      />

      <main className="main">
        <Topbar
          activeSection={activeSection}
          dateString={dateString}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        />

        <div className="content">
          <DashboardPage
            active={activeSection === 'dashboard'}
            leads={leads}
            tasks={tasks}
            dashFilter={dashFilter}
            setDashFilter={setDashFilter}
            onDownloadReport={() => openModal('downloadReport')}
            themeId={theme}
          />
          <PipelinePage
            active={activeSection === 'pipeline'}
            leads={leads}
            onAddLead={() => openModal('addLead')}
            onDeleteLead={handleRequestDeleteLead}
            onMoveLead={handleMoveLead}
          />
          <TasksPage
            active={activeSection === 'tasks'}
            tasks={tasks}
            onAddTask={() => openModal('addTask')}
            onToggleTask={handleToggleTask}
            onDeleteTask={handleDeleteTask}
          />
          <ContactsPage
            active={activeSection === 'contacts'}
            contacts={contacts}
            onAddContact={() => openModal('addContact')}
            onDeleteContact={handleDeleteContact}
          />
          <BillingPage
            active={activeSection === 'billing'}
            invoices={invoices}
            quotations={quotations}
            onAddInvoice={() => openModal('addInvoice')}
            onAddQuotation={() => openModal('addQuotation')}
            onDeleteInvoice={handleDeleteInvoice}
            onDeleteQuotation={handleDeleteQuotation}
            onInvoiceStatusChange={handleInvoiceStatusChange}
            onQuotationStatusChange={handleQuotationStatusChange}
          />
          <PreferencesPage
            active={activeSection === 'preferences'}
            themeId={theme}
            onSelectTheme={setTheme}
          />
        </div>
      </main>

      <ToastContainer toasts={toasts} />

      <AddLeadModal open={modals.addLead} onClose={() => closeModal('addLead')} onSubmit={handleAddLead} />
      <AddTaskModal open={modals.addTask} onClose={() => closeModal('addTask')} onSubmit={handleAddTask} />
      <AddContactModal open={modals.addContact} onClose={() => closeModal('addContact')} onSubmit={handleAddContact} />
      <AddInvoiceModal open={modals.addInvoice} onClose={() => closeModal('addInvoice')} onSubmit={handleAddInvoice} />
      <AddQuotationModal open={modals.addQuotation} onClose={() => closeModal('addQuotation')} onSubmit={handleAddQuotation} />
      <DeleteLeadModal
        open={modals.deleteLead}
        onClose={() => { closeModal('deleteLead'); setDeleteTarget(null); }}
        onConfirm={handleConfirmDeleteLead}
        target={deleteTarget}
      />
      <DownloadReportModal open={modals.downloadReport} onClose={() => closeModal('downloadReport')} onSubmit={handleDownloadReport} />
    </div>
  );
}
